import { CheckIcon } from '@phosphor-icons/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { Modal } from '@/components/ui/modal'
import { Select, SelectOption } from '@/components/ui/select'
import { Typography } from '@/components/ui/typography'
import { MarketShopInventoryItem } from '@/models'

import * as S from './change-category-modal.styled'

interface ChangeCategoryModalProps {
  items: MarketShopInventoryItem[]
  categoryOptions: SelectOption<number | string>[]
  onClose: () => void
  onConfirm: (categoryId: number | string) => Promise<void>
}

export const ChangeCategoryModal = ({
  items,
  categoryOptions,
  onClose,
  onConfirm,
}: ChangeCategoryModalProps) => {
  const { t } = useTranslation(['market-shop-dashboard', 'common'])
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleConfirm = async () => {
    if (selectedCategoryId == null) return
    setIsSubmitting(true)
    try {
      await onConfirm(selectedCategoryId)
      onClose()
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal
      size="small"
      title={t('market-shop-dashboard:inventory.changeCategoryModal.title')}
      footer={
        <>
          <Button use="secondary" size="medium" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            disabled={selectedCategoryId == null}
            loading={isSubmitting}
            size="medium"
            onClick={handleConfirm}
          >
            {t('common:actions.save')} <Icon icon={CheckIcon} />
          </Button>
        </>
      }
      onClose={onClose}
    >
      <S.ModalContent>
        <Typography color="black700" size="text-huge">
          {t('market-shop-dashboard:inventory.changeCategoryModal.description', {
            count: items.length,
          })}
        </Typography>
        <Select
          options={categoryOptions}
          placeholder={t('market-shop-dashboard:inventory.changeCategoryModal.placeholder')}
          size="medium"
          value={selectedCategoryId}
          helperText={t('market-shop-dashboard:inventory.changeCategoryModal.categoryHelperText')}
          onChange={(value) => setSelectedCategoryId(value)}
        />
      </S.ModalContent>
    </Modal>
  )
}
